import { useCallback, useSyncExternalStore } from "react";

/**
 * 指定間隔ごとに `Date.now()` を返す共有 tick。
 * 同じ intervalMs の購読者は 1 本の setInterval を共有する。
 * 例: `Intl.RelativeTimeFormat` で「5分前」等を表示している箇所が
 * 時間経過で陳腐化しないよう subscribe する。
 */

interface Channel {
	listeners: Set<() => void>;
	intervalId: ReturnType<typeof setInterval> | null;
	snapshot: number;
}

const channels = new Map<number, Channel>();

function getChannel(intervalMs: number): Channel {
	let channel = channels.get(intervalMs);
	if (!channel) {
		channel = { listeners: new Set(), intervalId: null, snapshot: Date.now() };
		channels.set(intervalMs, channel);
	}
	return channel;
}

/**
 * `intervalMs` ごとに再描画を発火する共有 tick。同間隔の購読者は 1 本の
 * setInterval を共有し、戻り値は最新の `Date.now()`。
 */
export function useTick(intervalMs: number): number {
	const subscribe = useCallback(
		(listener: () => void) => {
			const channel = getChannel(intervalMs);
			channel.listeners.add(listener);
			if (channel.intervalId === null) {
				channel.snapshot = Date.now();
				channel.intervalId = setInterval(() => {
					channel.snapshot = Date.now();
					for (const l of channel.listeners) l();
				}, intervalMs);
			}
			return () => {
				channel.listeners.delete(listener);
				if (channel.listeners.size === 0 && channel.intervalId !== null) {
					clearInterval(channel.intervalId);
					channel.intervalId = null;
				}
			};
		},
		[intervalMs],
	);
	return useSyncExternalStore(
		subscribe,
		() => getChannel(intervalMs).snapshot,
		() => getChannel(intervalMs).snapshot,
	);
}
